import React, { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const ImageUpload = () => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const navigate = useNavigate();
  
  // Get API URL from environment variables - use relative path in production
  const API_URL = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:5001/api');
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    // Check if file is selected
    if (!selectedFile) {
      return;
    }
    
    // Check file extension
    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'qcow' && fileExtension !== 'qcow2') {
      setError('Only .qcow and .qcow2 files are allowed');
      setFile(null);
      e.target.value = null;
      return;
    }
    
    // Check file size (max 10GB)
    const maxSize = 10 * 1024 * 1024 * 1024; // 10GB in bytes
    if (selectedFile.size > maxSize) {
      setError('File size exceeds the maximum limit of 10GB');
      setFile(null);
      e.target.value = null;
      return;
    }
    
    setFile(selectedFile);
    setError(null);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file to upload');
      return;
    }
    
    if (!name) {
      setError('Image name is required');
      return;
    }
    
    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);
      
      const formData = new FormData();
      formData.append('image', file);
      formData.append('name', name);
      formData.append('description', description);
      
      await axios.post(`${API_URL}/images/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      // Redirect to admin dashboard after successful upload
      navigate('/admin');
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Failed to upload image. Please try again.');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <UploadContainer>
      <h1>Upload QCOW Image</h1>
      
      <UploadForm onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="file">Select QCOW Image</Label>
          <FileInput
            type="file"
            id="file"
            onChange={handleFileChange}
            disabled={uploading}
            accept=".qcow,.qcow2"
          />
          <FileInputInfo>
            Max file size: 10GB. Allowed formats: .qcow, .qcow2
          </FileInputInfo>
          {file && (
            <SelectedFile>
              <strong>Selected file:</strong> {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
            </SelectedFile>
          )}
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="name">Image Name</Label>
          <Input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter image name"
            disabled={uploading}
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="description">Description (Optional)</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter image description"
            disabled={uploading}
            rows={4}
          />
        </FormGroup>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        {uploading && (
          <ProgressContainer>
            <ProgressBar progress={uploadProgress} />
            <ProgressText>{uploadProgress}% Uploaded</ProgressText>
          </ProgressContainer>
        )}
        
        <ButtonGroup>
          <CancelButton type="button" onClick={() => navigate('/admin')} disabled={uploading}>
            Cancel
          </CancelButton>
          <SubmitButton type="submit" disabled={uploading || !file}>
            {uploading ? 'Uploading...' : 'Upload Image'}
          </SubmitButton>
        </ButtonGroup>
      </UploadForm>
    </UploadContainer>
  );
};

// Styled components
const UploadContainer = styled.div`
  padding: ${props => props.theme.spacing.lg};
  max-width: 800px;
  margin: 0 auto;
  
  h1 {
    margin-bottom: ${props => props.theme.spacing.xl};
    color: ${props => props.theme.colors.opplaneBlue};
  }
`;

const UploadForm = styled.form`
  background-color: white;
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.medium};
  padding: ${props => props.theme.spacing.xl};
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${props => props.theme.spacing.sm};
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: ${props => props.theme.fontSizes.medium};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.opplaneBlue};
    box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.2);
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.background};
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: ${props => props.theme.fontSizes.medium};
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.opplaneBlue};
    box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.2);
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.background};
    cursor: not-allowed;
  }
`;

const FileInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.small};
  background-color: white;
  
  &:disabled {
    background-color: ${props => props.theme.colors.background};
    cursor: not-allowed;
  }
`;

const FileInputInfo = styled.div`
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.colors.lightText};
  margin-top: ${props => props.theme.spacing.xs};
`;

const SelectedFile = styled.div`
  margin-top: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm};
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: ${props => props.theme.fontSizes.small};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.xl};
`;

const SubmitButton = styled.button`
  flex: 1;
  background-color: ${props => props.theme.colors.opplaneBlue};
  color: white;
  padding: ${props => props.theme.spacing.md};
  border: none;
  border-radius: ${props => props.theme.borderRadius.small};
  font-weight: bold;
  cursor: pointer;
  transition: ${props => props.theme.transitions.short};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.opplaneDarkBlue};
  }
  
  &:disabled {
    background-color: rgba(0, 102, 204, 0.5);
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  background-color: white;
  color: ${props => props.theme.colors.text};
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.small};
  font-weight: bold;
  cursor: pointer;
  transition: ${props => props.theme.transitions.short};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.background};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(0, 102, 204, 0.1);
  color: ${props => props.theme.colors.error};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.small};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ProgressContainer = styled.div`
  margin: ${props => props.theme.spacing.md} 0;
`;

const ProgressBar = styled.div`
  height: 10px;
  background-color: #e9ecef;
  border-radius: ${props => props.theme.borderRadius.small};
  overflow: hidden;
  
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => props.progress}%;
    background-color: ${props => props.theme.colors.opplaneBlue};
    transition: width 0.3s ease;
  }
`;

const ProgressText = styled.div`
  font-size: ${props => props.theme.fontSizes.small};
  text-align: right;
  margin-top: ${props => props.theme.spacing.xs};
`;

export default ImageUpload;
